import { useEffect, useState } from 'react';
import { departmentsApi, businessUnitsApi, divisionsApi, gradesApi, categoriesApi } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, Briefcase, Loader2, Layers, Award, Tag } from 'lucide-react';

// Reusable EntityManager component for all entity types
const EntityManager = ({ 
  title, 
  description, 
  icon: Icon, 
  items, 
  onAdd, 
  onEdit, 
  onDelete, 
  onReactivate,
  addButtonLabel,
  emptyMessage,
  testIdPrefix
}) => {
  const activeItems = items.filter(i => i.is_active);
  const inactiveItems = items.filter(i => !i.is_active);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </div>
        <Button onClick={onAdd} data-testid={`add-${testIdPrefix}-btn`}>
          <Plus className="h-4 w-4 mr-2" />
          {addButtonLabel}
        </Button>
      </CardHeader>
      <CardContent>
        {activeItems.length === 0 ? (
          <div className="text-center py-12">
            <Icon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No {title.toLowerCase()} yet</h3>
            <p className="text-muted-foreground">{emptyMessage}</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activeItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="text-muted-foreground">{item.description || '-'}</TableCell>
                  <TableCell>
                    <Badge className="bg-green-100 text-green-800">Active</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEdit(item)}
                        data-testid={`edit-${testIdPrefix}-${item.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onDelete(item.id)}
                        data-testid={`delete-${testIdPrefix}-${item.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {inactiveItems.length > 0 && (
          <div className="mt-8">
            <h4 className="text-sm font-medium text-muted-foreground mb-4">Inactive {title}</h4>
            <Table>
              <TableBody>
                {inactiveItems.map((item) => (
                  <TableRow key={item.id} className="opacity-60">
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="text-muted-foreground">{item.description || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">Inactive</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => onReactivate(item)}>
                        Reactivate
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function OrgSettingsPage() {
  const [departments, setDepartments] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [grades, setGrades] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Generic dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState(null); // 'department', 'businessUnit', 'division', 'grade', 'category'
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [deptRes, buRes, divRes, gradeRes, catRes] = await Promise.all([
        departmentsApi.getAll(true),
        businessUnitsApi.getAll(true),
        divisionsApi.getAll(true),
        gradesApi.getAll(true),
        categoriesApi.getAll(true),
      ]);
      setDepartments(deptRes.data);
      setBusinessUnits(buRes.data);
      setDivisions(divRes.data);
      setGrades(gradeRes.data);
      setCategories(catRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load organization settings');
    } finally {
      setLoading(false);
    }
  };

  const getApiForType = (type) => {
    switch (type) {
      case 'department': return departmentsApi;
      case 'businessUnit': return businessUnitsApi;
      case 'division': return divisionsApi;
      case 'grade': return gradesApi;
      case 'category': return categoriesApi;
      default: return null;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'department': return 'Department';
      case 'businessUnit': return 'Business Unit';
      case 'division': return 'Division';
      case 'grade': return 'Grade';
      case 'category': return 'Category';
      default: return '';
    }
  };

  const handleOpenDialog = (type, item = null) => {
    setDialogType(type);
    setEditingItem(item);
    setFormData(item ? { name: item.name, description: item.description || '' } : { name: '', description: '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(`${getTypeLabel(dialogType)} name is required`);
      return;
    }
    
    const api = getApiForType(dialogType);
    setSubmitting(true);
    try {
      if (editingItem) {
        await api.update(editingItem.id, formData);
        toast.success(`${getTypeLabel(dialogType)} updated successfully`);
      } else {
        await api.create(formData);
        toast.success(`${getTypeLabel(dialogType)} created successfully`);
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error(error.response?.data?.detail || `Failed to save ${getTypeLabel(dialogType).toLowerCase()}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (type, id) => {
    if (!confirm(`Are you sure you want to deactivate this ${getTypeLabel(type).toLowerCase()}?`)) return;
    
    const api = getApiForType(type);
    try {
      await api.delete(id);
      toast.success(`${getTypeLabel(type)} deactivated`);
      loadData();
    } catch (error) {
      console.error('Failed to delete:', error);
      toast.error(error.response?.data?.detail || `Failed to deactivate ${getTypeLabel(type).toLowerCase()}`);
    }
  };

  const handleReactivate = async (type, item) => {
    const api = getApiForType(type);
    try {
      await api.update(item.id, { is_active: true });
      toast.success(`${getTypeLabel(type)} reactivated`);
      loadData();
    } catch (error) {
      console.error('Failed to reactivate:', error);
      toast.error(`Failed to reactivate ${getTypeLabel(type).toLowerCase()}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const activeDepartments = departments.filter(d => d.is_active);
  const activeBusinessUnits = businessUnits.filter(bu => bu.is_active);
  const activeDivisions = divisions.filter(d => d.is_active);
  const activeGrades = grades.filter(g => g.is_active);
  const activeCategories = categories.filter(c => c.is_active);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="org-settings-page">
      <div>
        <h1 className="text-3xl font-bold">Organization Settings</h1>
        <p className="text-muted-foreground mt-1">
          Manage organizational structure - departments, units, divisions, grades, and categories
        </p>
      </div>

      <Tabs defaultValue="business-units" className="space-y-6">
        <TabsList className="flex-wrap h-auto gap-1">
          <TabsTrigger value="business-units" className="flex items-center gap-2">
            <Briefcase className="h-4 w-4" />
            Units ({activeBusinessUnits.length})
          </TabsTrigger>
          <TabsTrigger value="divisions" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Divisions ({activeDivisions.length})
          </TabsTrigger>
          <TabsTrigger value="departments" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Departments ({activeDepartments.length})
          </TabsTrigger>
          <TabsTrigger value="grades" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Grades ({activeGrades.length})
          </TabsTrigger>
          <TabsTrigger value="categories" className="flex items-center gap-2">
            <Tag className="h-4 w-4" />
            Categories ({activeCategories.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="business-units">
          <EntityManager
            title="Business Units"
            description="Manage organizational business units"
            icon={Briefcase}
            items={businessUnits}
            onAdd={() => handleOpenDialog('businessUnit')}
            onEdit={(item) => handleOpenDialog('businessUnit', item)}
            onDelete={(id) => handleDelete('businessUnit', id)}
            onReactivate={(item) => handleReactivate('businessUnit', item)}
            addButtonLabel="Add Business Unit"
            emptyMessage="Add your first business unit to get started"
            testIdPrefix="bu"
          />
        </TabsContent>

        <TabsContent value="divisions">
          <EntityManager
            title="Divisions"
            description="Manage organizational divisions"
            icon={Layers}
            items={divisions}
            onAdd={() => handleOpenDialog('division')}
            onEdit={(item) => handleOpenDialog('division', item)}
            onDelete={(id) => handleDelete('division', id)}
            onReactivate={(item) => handleReactivate('division', item)}
            addButtonLabel="Add Division"
            emptyMessage="Add your first division to get started"
            testIdPrefix="division"
          />
        </TabsContent>

        <TabsContent value="departments">
          <EntityManager
            title="Departments"
            description="Manage organizational departments"
            icon={Building2}
            items={departments}
            onAdd={() => handleOpenDialog('department')}
            onEdit={(item) => handleOpenDialog('department', item)}
            onDelete={(id) => handleDelete('department', id)}
            onReactivate={(item) => handleReactivate('department', item)}
            addButtonLabel="Add Department"
            emptyMessage="Add your first department to get started"
            testIdPrefix="dept"
          />
        </TabsContent>

        <TabsContent value="grades">
          <EntityManager
            title="Grades"
            description="Manage employee grades/levels"
            icon={Award}
            items={grades}
            onAdd={() => handleOpenDialog('grade')}
            onEdit={(item) => handleOpenDialog('grade', item)}
            onDelete={(id) => handleDelete('grade', id)}
            onReactivate={(item) => handleReactivate('grade', item)}
            addButtonLabel="Add Grade"
            emptyMessage="Add your first grade to get started"
            testIdPrefix="grade"
          />
        </TabsContent>

        <TabsContent value="categories">
          <EntityManager
            title="Categories"
            description="Manage employee categories"
            icon={Tag}
            items={categories}
            onAdd={() => handleOpenDialog('category')}
            onEdit={(item) => handleOpenDialog('category', item)}
            onDelete={(id) => handleDelete('category', id)}
            onReactivate={(item) => handleReactivate('category', item)}
            addButtonLabel="Add Category"
            emptyMessage="Add your first category to get started"
            testIdPrefix="category"
          />
        </TabsContent>
      </Tabs>

      {/* Generic Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setFormData({ name: '', description: '' });
          setEditingItem(null);
          setDialogType(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingItem ? `Edit ${getTypeLabel(dialogType)}` : `Add ${getTypeLabel(dialogType)}`}
            </DialogTitle>
            <DialogDescription>
              {editingItem ? `Update ${getTypeLabel(dialogType).toLowerCase()} information` : `Create a new ${getTypeLabel(dialogType).toLowerCase()}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="item-name">Name</Label>
                <Input
                  id="item-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={`e.g., ${dialogType === 'grade' ? 'Grade A' : dialogType === 'category' ? 'Full-Time' : 'Operations'}`}
                  required
                  data-testid="entity-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="item-desc">Description (Optional)</Label>
                <Input
                  id="item-desc"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description"
                  data-testid="entity-desc-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} data-testid="save-entity-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editingItem ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
